import React from 'react';

import { ConfluenceUpgradeFreePoolExperimentModalPreUpgrade } from '@atlassian/upgrade-free-pool-target-experiment';
import { ErrorType, Severity } from '@atlassian/growth-commons-error-analytics';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance';

import { ExperimentErrorBoundaryWrapper } from '../common/ErrorBoundaryWrapper';
import { useExperimentErrorAnalytics } from '../utils/useExperimentErrorAnalytics';
import { ConfluenceFreePoolTargetExperimentQuery } from '../pre-upgrade-modal-graphql-query/ConfluenceFreePoolTargetExperimentQuery';
import type {
	ConfluenceFreePoolTargetExperimentQuery$data,
	ConfluenceFreePoolTargetExperimentQuery$variables,
} from '../pre-upgrade-modal-graphql-query/__generated__/ConfluenceFreePoolTargetExperimentQuery.graphql';

import { useIsEligibleForExperiment } from './useIsEligibleForExperiment';
import { useUpgradeFreePoolTargetExperimentCohort } from './useUpgradeFreePoolTargetExperimentCohort';

export const UpgradeFreePoolExperimentModalPreUpgradeModalBaseComponent = () => {
	const { cloudId, edition } = useSessionData();
	const { trackError } = useExperimentErrorAnalytics({
		defaultAdditionalAttributes: {
			edition,
		},
	});

	const canSkipQuery = !cloudId || !edition || edition !== ConfluenceEdition.FREE;

	const { data } = useQueryInterop<
		ConfluenceFreePoolTargetExperimentQuery$data,
		ConfluenceFreePoolTargetExperimentQuery$variables
	>(ConfluenceFreePoolTargetExperimentQuery, {
		variables: {
			cloudId,
			productKey: 'confluence.ondemand',
		},
		skip: canSkipQuery,
		fetchPolicy: 'cache-first', // for SSR'ing purposes
		client: getAGGClient(),
		onError: (graphqlError) => {
			trackError({
				errorType: ErrorType.GRAPHQL,
				errorMessage: graphqlError.message,
				severity: Severity.HIGH,
				source: 'ConfluenceFreePoolTargetExperimentQuery',
				cohort: undefined,
				additionalAttributes: {
					variables: {
						cloudId,
						productKey: 'confluence.ondemand',
					},
					canSkipQuery,
					errorStack: graphqlError.stack,
					errorName: graphqlError.name,
				},
			});
		},
	});

	const {
		isEligible: isEligibleForExperiment,
		experienceUrl,
		analyticsAttributes,
	} = useIsEligibleForExperiment(data);

	const { abCohort } = useUpgradeFreePoolTargetExperimentCohort({
		isEligible: isEligibleForExperiment,
		analyticsAttributes,
	});

	if (abCohort !== 'variation') {
		return null;
	}

	const confluenceStorageAdapter = {
		get: (key: string) => {
			return confluenceLocalStorageInstance.getItem(key);
		},
		set: (key: string, value: string) => {
			confluenceLocalStorageInstance.setItem(key, value);
		},
		removeItem: (key: string) => {
			confluenceLocalStorageInstance.removeItem(key);
		},
	};

	return (
		<ExperimentErrorBoundaryWrapper
			experimentKey="twcg-206_confluence_upgrade_permanent_free"
			source="UpgradeFreePoolExperimentModalPreUpgradeModalBaseComponent"
			cohort={abCohort}
			additionalAttributes={{
				cloudId,
				edition,
				experienceUrl,
				isEligibleForExperiment,
				analyticsAttributes,
			}}
		>
			<ConfluenceUpgradeFreePoolExperimentModalPreUpgrade
				storageAdapter={confluenceStorageAdapter}
				experienceUrl={experienceUrl}
				analyticsAttributes={analyticsAttributes}
			/>
		</ExperimentErrorBoundaryWrapper>
	);
};
